import { AxiosPromise } from 'axios';

import { mmHttp, MmRequestConfig } from './mmHttp';
import { buildPageQuery, MmPaginationState, readPage } from './pagination';
import { MmPage } from '../types/dto';
import {
  MmZoningAssignment,
  MmZoningAssignPayload,
  MmZoningCompetitor,
  MmZoningModelEval,
  MmZoningModelLine,
  MmZoningModelUploadPayload,
  MmZoningModelVersion,
  MmZoningUploadDryRunReport,
} from '../types/zoningModels';

const ZONING_PATH = '/ml/zoning';
const MODELS_SEGMENT = '/models';
const ASSIGNMENTS_SEGMENT = '/assignments';
const COMPETITORS_SEGMENT = '/competitors';

function readText(value: any): string {
  return typeof value === 'string' ? value : '';
}

function readNumber(value: any): number {
  return typeof value === 'number' && !isNaN(value) ? value : 0;
}

function readBool(value: any): boolean {
  return value === true;
}

function readIsoOrNull(value: any): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function readEval(raw: any): MmZoningModelEval {
  const source = raw && typeof raw === 'object' ? raw : {};

  return {
    metric: readText(source.metric),
    deltaPp: readNumber(source.deltaPp),
    ciLow: readNumber(source.ciLow),
    ciHigh: readNumber(source.ciHigh),
    n: readNumber(source.n),
    reportLink: readText(source.reportLink),
  };
}

function readVersion(raw: any): MmZoningModelVersion | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const id = readText(raw.id);

  if (id === '') {
    return null;
  }

  return {
    id: id,
    lineId: readText(raw.lineId),
    seq: readNumber(raw.seq),
    version: readText(raw.version),
    sizeBytes: readNumber(raw.sizeBytes),
    sha256: readText(raw.sha256),
    detSha256: readText(raw.detSha256),
    coordSpace: readText(raw.coordSpace),
    trainedOn: readText(raw.trainedOn),
    featureSet: readText(raw.featureSet),
    isActive: readBool(raw.isActive),
    validatedAt: readIsoOrNull(raw.validatedAt),
    createdAt: readText(raw.createdAt),
    evaluation: readEval(raw.evaluation),
  };
}

function readLine(raw: any): MmZoningModelLine | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const id = readText(raw.id);

  if (id === '') {
    return null;
  }

  const versions: MmZoningModelVersion[] = [];

  if (Array.isArray(raw.versions)) {
    for (let i = 0; i < raw.versions.length; i += 1) {
      const version = readVersion(raw.versions[i]);

      if (version !== null) {
        versions.push(version);
      }
    }
  }

  let active: MmZoningModelVersion | null = readVersion(raw.activeVersion);

  if (active === null) {
    for (let j = 0; j < versions.length; j += 1) {
      if (versions[j].isActive) {
        active = versions[j];
        break;
      }
    }
  }

  return {
    id: id,
    modelKey: readText(raw.modelKey),
    title: readText(raw.title),
    createdAt: readText(raw.createdAt),
    activeVersion: active,
    versions: versions,
    assignmentsCount: readNumber(raw.assignmentsCount),
  };
}

function readAssignment(raw: any): MmZoningAssignment | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const id = readText(raw.id);

  if (id === '') {
    return null;
  }

  return {
    id: id,
    organizationId: readText(raw.organizationId),
    competitorId: readText(raw.competitorId),
    competitorName: readText(raw.competitorName),
    lineId: readText(raw.lineId),
    lineKey: readText(raw.lineKey),
    lineTitle: readText(raw.lineTitle),
    assignedAt: readText(raw.assignedAt),
  };
}

function readCompetitor(raw: any): MmZoningCompetitor | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const id = readText(raw.id);

  if (id === '') {
    return null;
  }

  return {
    id: id,
    nameNormalized: readText(raw.nameNormalized),
    displayName: readText(raw.displayName),
  };
}

export function fetchZoningModelLines(
  pagination: MmPaginationState,
  config?: MmRequestConfig,
): Promise<MmPage<MmZoningModelLine>> {
  const params = buildPageQuery(pagination);

  return mmHttp
    .get(
      ZONING_PATH + MODELS_SEGMENT,
      Object.assign({ params: params, mmSection: 'zoning/models' }, config),
    )
    .then((response) => {
      const pageRaw = readPage<any>(response.data);
      const items: MmZoningModelLine[] = [];

      for (let i = 0; i < pageRaw.content.length; i += 1) {
        const line = readLine(pageRaw.content[i]);

        if (line !== null) {
          items.push(line);
        }
      }

      return {
        content: items,
        totalElements: pageRaw.totalElements,
        totalKnown: pageRaw.totalKnown,
        hasMore: pageRaw.hasMore,
      };
    });
}

export function fetchZoningAssignments(
  pagination: MmPaginationState,
  config?: MmRequestConfig,
): Promise<MmPage<MmZoningAssignment>> {
  const params = buildPageQuery(pagination);

  return mmHttp
    .get(
      ZONING_PATH + ASSIGNMENTS_SEGMENT,
      Object.assign({ params: params, mmSection: 'zoning/assignments' }, config),
    )
    .then((response) => {
      const pageRaw = readPage<any>(response.data);
      const items: MmZoningAssignment[] = [];

      for (let i = 0; i < pageRaw.content.length; i += 1) {
        const row = readAssignment(pageRaw.content[i]);

        if (row !== null) {
          items.push(row);
        }
      }

      return {
        content: items,
        totalElements: pageRaw.totalElements,
        totalKnown: pageRaw.totalKnown,
        hasMore: pageRaw.hasMore,
      };
    });
}

export function fetchZoningCompetitors(config?: MmRequestConfig): Promise<MmZoningCompetitor[]> {
  return mmHttp
    .get(
      ZONING_PATH + COMPETITORS_SEGMENT,
      Object.assign({ mmSection: 'zoning/competitors' }, config),
    )
    .then((response) => {
      const pageRaw = readPage<any>(response.data);
      const items: MmZoningCompetitor[] = [];

      for (let i = 0; i < pageRaw.content.length; i += 1) {
        const row = readCompetitor(pageRaw.content[i]);

        if (row !== null) {
          items.push(row);
        }
      }

      return items;
    });
}

/** axios 0.18 не трогает FormData, Content-Type с boundary браузер проставит сам — руками не задавать */
function buildUploadFormData(file: File, payload: MmZoningModelUploadPayload): FormData {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('modelKey', payload.modelKey);
  formData.append('title', payload.title);
  formData.append('evalMetric', payload.evaluation.metric);
  formData.append('evalDeltaPp', String(payload.evaluation.deltaPp));
  formData.append('evalCiLow', String(payload.evaluation.ciLow));
  formData.append('evalCiHigh', String(payload.evaluation.ciHigh));
  formData.append('evalN', String(payload.evaluation.n));
  formData.append('evalReportLink', payload.evaluation.reportLink);

  return formData;
}

export function dryRunZoningModelUpload(
  file: File,
  payload: MmZoningModelUploadPayload,
  config?: MmRequestConfig,
): Promise<MmZoningUploadDryRunReport> {
  return mmHttp
    .post(
      ZONING_PATH + MODELS_SEGMENT,
      buildUploadFormData(file, payload),
      Object.assign({ params: { dryRun: true }, mmSection: 'zoning/models/dryRun' }, config),
    )
    .then((response) => {
      const data = response.data && typeof response.data === 'object' ? response.data : {};
      const checks: MmZoningUploadDryRunReport['checks'] = [];

      if (Array.isArray(data.checks)) {
        for (let i = 0; i < data.checks.length; i += 1) {
          const raw = data.checks[i];

          if (raw && typeof raw === 'object') {
            checks.push({
              key: readText(raw.key),
              passed: readBool(raw.passed),
              detail: readText(raw.detail),
            });
          }
        }
      }

      return {
        ok: readBool(data.ok),
        sha256: readText(data.sha256),
        sizeBytes: readNumber(data.sizeBytes),
        checks: checks,
      };
    });
}

export function uploadZoningModel(
  file: File,
  payload: MmZoningModelUploadPayload,
  config?: MmRequestConfig,
): Promise<MmZoningModelLine | null> {
  return mmHttp
    .post(
      ZONING_PATH + MODELS_SEGMENT,
      buildUploadFormData(file, payload),
      Object.assign({ mmSection: 'zoning/models/upload' }, config),
    )
    .then((response) => readLine(response.data));
}

/** Без заполненных метрик оценки сервер ответит 422 */
export function activateZoningModelVersion(
  versionId: string,
  config?: MmRequestConfig,
): AxiosPromise<any> {
  return mmHttp.post(
    ZONING_PATH + MODELS_SEGMENT + '/' + encodeURIComponent(versionId) + '/activate',
    null,
    Object.assign({ mmSection: 'zoning/models/activate' }, config),
  );
}

export function deactivateZoningModelVersion(
  versionId: string,
  config?: MmRequestConfig,
): AxiosPromise<any> {
  return mmHttp.post(
    ZONING_PATH + MODELS_SEGMENT + '/' + encodeURIComponent(versionId) + '/deactivate',
    null,
    Object.assign({ mmSection: 'zoning/models/deactivate' }, config),
  );
}

export function assignZoningModel(
  payload: MmZoningAssignPayload,
  config?: MmRequestConfig,
): AxiosPromise<any> {
  return mmHttp.post(
    ZONING_PATH + ASSIGNMENTS_SEGMENT,
    { competitorId: payload.competitorId, lineId: payload.lineId },
    Object.assign({ mmSection: 'zoning/assignments/create' }, config),
  );
}
